import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from '@cbrebuild/blocks';
import TopNavPublic from '../../components/navs/top-nav-public';
import verificationService from '../../services/verification-service';
import './voucher-verify.scss';

import {
  COMMISSION_ATTACHMENT_KEY,
  FIELD_KEY_HAS_COMMISSION_AGREEMENT,
  FIELD_KEY_COMMISSION_AGREEMENT,
  BOUNDS_CACHE_KEY,
  FIELD_KEY_IS_FULLY_EXECUTED_LEASE,
  FIELD_KEY_FULLY_EXECUTED_LEASE,
  LEGACY_COMMISSION_ATTACHMENT_KEY,
  EMPTY_OUTLIER_RANGES,
  SQUARE_FOOTAGE_FIELD_KEYS,
} from './voucher-verify-constants';

import {
  buildOutlierRangesFromBounds,
  formatDateTime,
  normalizeField,
  checkFieldOutlier,
  getLeaseDuration,
  isEmptyVerificationValue,
  getRequiredFieldErrorMessage,
  resolveDateReferenceRanges,
} from './voucher-verify-utils';

import ConfirmationScreen from './components/ConfirmationScreen';
import FieldRow from './components/FieldRow';

const OPTIONAL_VERIFICATION_FIELD_KEYS = new Set(['floor', 'operating_expenses_amount_per_sqft']);

const VoucherVerifyPage = ({
  match, history, setShowGlobalNavigation, toast,
}) => {
  const dealId = match?.params?.dealId;

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [request, setRequest] = useState(null);
  const [fieldValues, setFieldValues] = useState({});
  const [attachments, setAttachments] = useState({});
  const [autosaving, setAutosaving] = useState(false);
  const [savingFieldKeys, setSavingFieldKeys] = useState({});
  const [termMissingReason, setTermMissingReason] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [confirmed, setConfirmed] = useState(null);
  const [outlierRanges, setOutlierRanges] = useState(EMPTY_OUTLIER_RANGES);
  const [acceptedOutlierValues, setAcceptedOutlierValues] = useState({});
  const acknowledgementScrollRef = useRef(null);

  const autosaveReadyRef = useRef(false);
  const lastSavedPayloadRef = useRef(null);
  const saveGenerationRef = useRef(0);
  const pendingSaveCountRef = useRef(0);

  useEffect(() => {
    try {
      const cachedRaw = window.sessionStorage.getItem(BOUNDS_CACHE_KEY);
      if (cachedRaw) {
        const cachedBounds = JSON.parse(cachedRaw);
        const cachedRanges = buildOutlierRangesFromBounds(cachedBounds);
        if (Object.keys(cachedRanges).length > 0) {
          setOutlierRanges(cachedRanges);
        }
      }
    } catch (e) {
      // ignore cache read errors
    }

    verificationService.getVerificationBounds()
      .then((data) => {
        const ranges = buildOutlierRangesFromBounds(data);
        setOutlierRanges(ranges);
        try {
          window.sessionStorage.setItem(BOUNDS_CACHE_KEY, JSON.stringify(data || {}));
        } catch (e) {
          // ignore cache write errors
        }
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (setShowGlobalNavigation) {
      setShowGlobalNavigation(false);
      return () => setShowGlobalNavigation(true);
    }
    return undefined;
  }, [setShowGlobalNavigation]);

  const initializeFromData = useCallback((data) => {
    const mappedFields = (data.fields || []).map(normalizeField);

    // client_name's label already flips to Tenant/Landlord Name based on rep_role;
    // pin it and its matching rep-company field (same side) always to the top.
    const clientNameField = mappedFields.find(f => f.key === 'client_name');
    const pinnedRepCompanyLabel = clientNameField?.label === 'Landlord Name'
      ? 'Landlord Rep Company'
      : 'Tenant Rep Company';

    const normalizedFields = mappedFields
      .sort((a, b) => {
        const rank = (f) => {
          if (f.key === 'client_name') return 0;
          if (f.label === pinnedRepCompanyLabel) return 1;
          if (f.isRequiredQuestion) return 4;
          if (f.isEditable || f.key === 'lease_term_months') return 3;
          return 2;
        };
        return rank(a) - rank(b);
      });

    const initialValues = {};
    normalizedFields.forEach((field) => {
      if (field.isEditable) {
        initialValues[field.key] = field.currentValue;
      }
    });

    const initialAttachments = data.attachments || {
      [COMMISSION_ATTACHMENT_KEY]: data.commission_agreement_attachments
        || data[COMMISSION_ATTACHMENT_KEY]
        || data[LEGACY_COMMISSION_ATTACHMENT_KEY]
        || [],
    };
    setRequest({ ...data, fields: normalizedFields });
    setFieldValues(initialValues);
    setAttachments(initialAttachments);
    setAcceptedOutlierValues({});

    lastSavedPayloadRef.current = JSON.stringify({
      fields: initialValues,
      attachments: initialAttachments,
    });
    autosaveReadyRef.current = true;
  }, []);

  // super-fetch skips its generic 403 redirect for voucher-verify URLs so the dashboard
  // is reached directly instead of via '/' -> <Redirect to="/dashboard">
  const redirectToDashboard = useCallback(() => history.push('/dashboard'), [history]);

  // Re-fetch so BACK (after a successful verify) shows the server's latest state
  // (verification_status, verified_by/date, live field values) instead of stale local state.
  const loadVerificationData = useCallback(() => {
    setLoading(true);
    verificationService.getVerificationRequest(dealId)
      .then((data) => {
        setLoadError(null);
        initializeFromData(data);
        setLoading(false);
      })
      .catch((err) => {
        // 403 is an access issue -- bounce to the dashboard like deal-detail-page does,
        // staying in the loading state so no error screen flashes before navigation lands
        if (err?.status === 403) {
          redirectToDashboard();
          return;
        }
        setLoadError(err?.detail || err?.message || 'Failed to load verification data from API.');
        setLoading(false);
      });
  }, [dealId, initializeFromData, redirectToDashboard]);

  useEffect(() => {
    if (!dealId || dealId === 'test') {
      setLoadError('Invalid verification link.');
      setLoading(false);
      return;
    }

    loadVerificationData();
  }, [dealId, loadVerificationData]);

  const handleFieldChange = useCallback((key, value) => {
    const hasValue = v => !(v === '' || v === null || v === undefined);
    const isSelectedEditableField = fieldKey => request?.fields?.some(field => (
      field.key === fieldKey && field.isEditable
    ));
    const changedKeys = [key];
    if (key === 'rent_escalation_dollar'
      && hasValue(value)
      && isSelectedEditableField('rent_escalation_percent')) {
      changedKeys.push('rent_escalation_percent');
    }
    if (key === 'rent_escalation_percent'
      && hasValue(value)
      && isSelectedEditableField('rent_escalation_dollar')) {
      changedKeys.push('rent_escalation_dollar');
    }

    setFieldValues((prev) => {
      const next = { ...prev, [key]: value };

      // Keep lease escalation inputs mutually exclusive -- 0, not null, so the
      // unset side is stored as a real value when both fields are selected.
      if (key === 'rent_escalation_dollar'
        && hasValue(value)
        && isSelectedEditableField('rent_escalation_percent')) {
        next.rent_escalation_percent = 0;
      }
      if (key === 'rent_escalation_percent'
        && hasValue(value)
        && isSelectedEditableField('rent_escalation_dollar')) {
        next.rent_escalation_dollar = 0;
      }

      return next;
    });
    setAcceptedOutlierValues((prev) => {
      const next = { ...prev };
      changedKeys.forEach(fieldKey => delete next[fieldKey]);
      return next;
    });
  }, [request]);

  // Recompute the read-only Term from the populated lease dates.
  useEffect(() => {
    const termField = (request?.fields || []).find(
      field => field.key === 'lease_term_months'
    );
    if (!termField) return;
    const startDate = fieldValues.date_lease_from
      ?? request?.fields?.find(field => field.key === 'date_lease_from')?.currentValue
      ?? request?.date_reference_values?.date_lease_from;
    const endDate = fieldValues.lease_expiration_date
      ?? request?.fields?.find(field => field.key === 'lease_expiration_date')?.currentValue
      ?? request?.date_reference_values?.lease_expiration_date
      ?? request?.lease_expiration_date;
    const computed = getLeaseDuration(startDate, endDate);
    const termKey = termField.key;
    setFieldValues((prev) => {
      const nextValue = computed === null ? '' : computed;
      if (prev[termKey] === nextValue) return prev;
      return { ...prev, [termKey]: nextValue };
    });

    const termRange = outlierRanges.lease_term_months;
    const minimumTerm = termRange?.min != null ? Number(termRange.min) : null;
    const maximumTerm = termRange?.max != null ? Number(termRange.max) : null;
    const hasTermBounds = Number.isFinite(minimumTerm) && Number.isFinite(maximumTerm);
    const isTermValid = computed !== null
      && (!hasTermBounds || (computed >= minimumTerm && computed <= maximumTerm));
    const rangeMessage = hasTermBounds
      ? `Typical range: ${minimumTerm} - ${maximumTerm} months`
      : null;
    setTermMissingReason(isTermValid
      ? null
      : [rangeMessage, 'Please review and adjust your Lease Start Date and Expiration Date']
        .filter(Boolean)
        .join('\n'));
  }, [request, fieldValues.date_lease_from, fieldValues.lease_expiration_date, outlierRanges]);

  const getEditableFields = useCallback((fields) => {
    const editableFields = { ...(fields || {}) };
    delete editableFields.lease_term_months;
    return editableFields;
  }, []);

  // Lease Term is derived from the dates and never saved, so it must not count as unsaved.
  const serializeVerificationState = useCallback(state => JSON.stringify({
    ...state,
    fields: getEditableFields(state.fields),
  }), [getEditableFields]);

  const buildPayloadFromState = useCallback((state) => {
    const lastSaved = lastSavedPayloadRef.current
      ? JSON.parse(lastSavedPayloadRef.current)
      : {};
    const lastSavedFields = lastSaved.fields || {};
    const lastSavedAttachments = lastSaved.attachments || {};
    const nextFields = getEditableFields(state.fields);
    const nextAttachments = state.attachments || {};

    const changedFields = Object.fromEntries(Object.entries(nextFields).filter(([key, val]) => JSON.stringify(val) !== JSON.stringify(lastSavedFields[key])));

    // only include attachments if they actually changed
    const payload = { fields: changedFields };
    if (JSON.stringify(nextAttachments) !== JSON.stringify(lastSavedAttachments)) {
      payload.attachments = nextAttachments;
    }
    return payload;
  }, [getEditableFields]);

  const saveVerificationState = useCallback((state, fieldKey = null, fieldOverrides = null) => {
    if (!autosaveReadyRef.current || !request || confirmed) return;

    const payload = buildPayloadFromState(state);
    Object.entries(fieldOverrides || {}).forEach(([key, value]) => {
      delete payload.fields[key];
      payload.fields[key] = value;
    });
    const fullState = serializeVerificationState(state);
    if (fullState === lastSavedPayloadRef.current) return;

    const saveGeneration = saveGenerationRef.current + 1;
    saveGenerationRef.current = saveGeneration;
    pendingSaveCountRef.current += 1;
    setAutosaving(true);
    if (fieldKey) {
      setSavingFieldKeys(prev => ({ ...prev, [fieldKey]: (prev[fieldKey] || 0) + 1 }));
    }

    verificationService.saveVerification(dealId, payload)
      .then(() => {
        setActionError(null);
        if (saveGeneration === saveGenerationRef.current) {
          lastSavedPayloadRef.current = fullState;
        }
      })
      .catch((err) => {
        if (err?.status === 403) {
          redirectToDashboard();
          return;
        }
        const detail = err?.detail || 'Autosave failed. Please try again.';
        setActionError(detail);
      })
      .finally(() => {
        pendingSaveCountRef.current -= 1;
        if (pendingSaveCountRef.current === 0) setAutosaving(false);
        if (fieldKey) {
          setSavingFieldKeys((prev) => {
            const next = { ...prev };
            if (next[fieldKey] > 1) next[fieldKey] -= 1;
            else delete next[fieldKey];
            return next;
          });
        }
      });
  }, [buildPayloadFromState, confirmed, dealId, request, serializeVerificationState]);

  const handleEscalationModeChange = useCallback((fieldKey, isCustom, value) => {
    saveVerificationState({
      fields: { ...fieldValues, [fieldKey]: value },
      attachments,
    }, fieldKey, { [fieldKey]: isCustom });
  }, [attachments, fieldValues, saveVerificationState]);

  const handleFieldBlur = useCallback((fieldKey) => {
    saveVerificationState({ fields: fieldValues, attachments }, fieldKey);
  }, [attachments, fieldValues, saveVerificationState]);

  // Calendar picks and schedule removals must save their committed value without waiting for blur.
  const handleFieldCommit = useCallback((fieldKey, value) => {
    saveVerificationState({ fields: { ...fieldValues, [fieldKey]: value }, attachments }, fieldKey);
  }, [attachments, fieldValues, saveVerificationState]);

  const handleBooleanChange = useCallback((fieldKey, value) => {
    handleFieldChange(fieldKey, value);
    saveVerificationState({ fields: { ...fieldValues, [fieldKey]: value }, attachments }, fieldKey);
  }, [attachments, fieldValues, handleFieldChange, saveVerificationState]);

  const handleAttachmentsChange = useCallback((key, ids) => {
    const nextAttachments = { ...attachments, [key]: ids };
    setAttachments(nextAttachments);
    saveVerificationState({ fields: fieldValues, attachments: nextAttachments });
  }, [attachments, fieldValues, saveVerificationState]);

  const visibleFields = useMemo(() => {
    const fields = request?.fields || [];
    const selectedLayoutFields = request?.deal_field_layout || [];
    return fields.filter((field) => {
      if (field.key !== 'lease_term_months') return true;
      return selectedLayoutFields.some(layoutField => (
        layoutField.is_included
        && layoutField.field_name === field.key
      ));
    });
  }, [request]);

  const allVisibleEditableFieldsFilled = useMemo(() => visibleFields
    .filter(field => field.isEditable && !OPTIONAL_VERIFICATION_FIELD_KEYS.has(field.key))
    .every((field) => {
      const val = fieldValues[field.key] !== undefined ? fieldValues[field.key] : field.currentValue;
      return !isEmptyVerificationValue(val);
    }), [visibleFields, fieldValues]);

  const hasCommissionAgreement = (
    fieldValues[FIELD_KEY_HAS_COMMISSION_AGREEMENT] === true
    || fieldValues[FIELD_KEY_COMMISSION_AGREEMENT] === true
  );
  const hasCommissionDoc = (attachments[COMMISSION_ATTACHMENT_KEY] || []).length > 0;
  const dateReferenceValues = request?.date_reference_values || EMPTY_OUTLIER_RANGES;
  const resolvedOutlierRanges = useMemo(
    () => resolveDateReferenceRanges(outlierRanges, fieldValues, dateReferenceValues),
    [outlierRanges, fieldValues, dateReferenceValues]
  );
  const activeOutlierFields = useMemo(() => (
    (request?.fields || []).filter((field) => {
      if (!field.isEditable) return false;
      const val = fieldValues[field.key] !== undefined ? fieldValues[field.key] : field.currentValue;
      return checkFieldOutlier(field, val, resolvedOutlierRanges);
    })
  ), [request, fieldValues, resolvedOutlierRanges]);
  const hasActiveOutliers = activeOutlierFields.length > 0;
  const activeOutlierValues = activeOutlierFields.map((field) => ({
    key: field.key,
    value: fieldValues[field.key] !== undefined ? fieldValues[field.key] : field.currentValue,
  }));
  const hasAcceptedOutliers = activeOutlierValues.length > 0
    && activeOutlierValues.every(({ key, value }) => (
      Object.prototype.hasOwnProperty.call(acceptedOutlierValues, key)
      && acceptedOutlierValues[key] === JSON.stringify([typeof value, value])
    ));
  const hasUnsavedChanges = autosaveReadyRef.current
    && serializeVerificationState({ fields: fieldValues, attachments })
      !== lastSavedPayloadRef.current;
  const isVerifyEnabled = allVisibleEditableFieldsFilled
    && (!hasCommissionAgreement || hasCommissionDoc)
    && (!hasActiveOutliers || hasAcceptedOutliers)
    && !hasUnsavedChanges
    && !verifying
    && !autosaving;

  const handleVerify = () => {
    const payload = {
      fields: getEditableFields(fieldValues),
      attachments,
      accept_outlier_values: hasAcceptedOutliers,
    };

    setVerifying(true);
    verificationService.submitVerification(dealId, payload)
      .then((data) => {
        setActionError(null);
        setConfirmed({
          verifiedAt: data.verified_date,
          verifiedBy: data.verified_by || '',
          notifiedRecipients: data.notified_recipients || [],
        });
      })
      .catch((err) => {
        if (err?.status === 403) {
          redirectToDashboard();
          return;
        }
        const detail = err?.detail || 'Verification failed. Please try again.';
        setActionError(detail);
      })
      .finally(() => setVerifying(false));
  };

  const handleBack = () => {
    setConfirmed(null);
    loadVerificationData();
  };

  const handleDone = () => {
    if (toast && request?.deal_name) {
      const verifierName = confirmed?.verifiedBy;
      const msg = verifierName
        ? `${verifierName} validated ${request.deal_name}`
        : `Voucher verified: ${request.deal_name}`;
      toast(msg);
    }
    history.push('/dashboard');
  };

  const renderContent = () => {
    if (loading) {
      return <div className="verify-loading">Loading verification form...</div>;
    }

    if (loadError) {
      return (
        <div className="verify-error">
          <h2>Verification Unavailable</h2>
          <p>{loadError}</p>
        </div>
      );
    }

    if (confirmed) {
      return (
        <ConfirmationScreen
          verifiedAt={confirmed.verifiedAt}
          notifiedRecipients={confirmed.notifiedRecipients}
          onBack={handleBack}
          onDone={handleDone}
        />
      );
    }

    const {
      deal_name,
      email_body,
      fields = [],
      verified_by: lastVerifiedBy,
      verified_date: lastVerifiedDate,
    } = request;

    const lastVerifierName = lastVerifiedBy?.name || lastVerifiedBy?.email || '';
    return (
      <>
        <div className="verify-header">
          {email_body && (
            <div className="greeting">
              {email_body.split(/\n{2,}/).map((para, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <p key={idx}>{para.trim()}</p>
              ))}
            </div>
          )}
          <h1 className="section-title">Voucher verification</h1>
          <h2 className={`deal-name${request.property_location ? ' has-property-address' : ''}`}>{deal_name}</h2>
          {request.property_location && (
            <div className="property-address">{request.property_location}</div>
          )}
          {actionError && <div className="verify-inline-error">{actionError}</div>}
        </div>

        <div className="verify-fields">
          <p className="section-label">Deal Info &amp; Terms</p>
          {visibleFields.map((field) => {
            const displayValue = fieldValues[field.key] !== undefined
              ? fieldValues[field.key]
              : field.currentValue;
            const isRequiredField = field.isEditable && !OPTIONAL_VERIFICATION_FIELD_KEYS.has(field.key);
            const isRequiredIndicator = isRequiredField || field.key === 'lease_term_months';
            const requiredErrorMessage = isRequiredField
              && isEmptyVerificationValue(displayValue)
              ? getRequiredFieldErrorMessage(
                field.key === 'rent_escalation_is_custom' || field.key === 'lease_escalation'
                  ? 'Lease Escalation'
                  : field.label
              )
              : null;

            return (
              <FieldRow
                key={field.key}
                field={field}
                fieldValues={fieldValues}
                onChange={handleFieldChange}
                onBooleanChange={handleBooleanChange}
                onEscalationModeChange={handleEscalationModeChange}
                onEscalationScheduleCommit={handleFieldCommit}
                onDateCommit={handleFieldCommit}
                onBlur={handleFieldBlur}
                dealId={request.deal_id}
                attachments={attachments}
                onAttachmentsChange={handleAttachmentsChange}
                outlierRanges={resolvedOutlierRanges}
                isSaving={(field.key === 'floor' || SQUARE_FOOTAGE_FIELD_KEYS.includes(field.key))
                  && Boolean(savingFieldKeys[field.key])}
                requiredErrorMessage={requiredErrorMessage}
                emptyReason={field.key === 'lease_term_months' ? termMissingReason : null}
                isRequired={isRequiredIndicator}
              />
            );
          })}
        </div>

        <div className="verify-actions">
          {hasActiveOutliers && allVisibleEditableFieldsFilled && (
            <div
              className="verify-outlier-acknowledgement"
              onMouseDownCapture={(event) => {
                acknowledgementScrollRef.current = { x: window.scrollX, y: window.scrollY };
                event.preventDefault();
              }}
              onClick={() => {
                const scrollPosition = acknowledgementScrollRef.current;
                acknowledgementScrollRef.current = null;
                if (!scrollPosition) return;
                const restoreScroll = () => window.scrollTo(scrollPosition.x, scrollPosition.y);
                window.requestAnimationFrame(() => {
                  restoreScroll();
                  window.setTimeout(restoreScroll, 0);
                });
              }}
            >
              <Checkbox
                id="accept-outlier-values"
                checked={hasAcceptedOutliers}
                onChange={({ target: { checked } }) => {
                  if (!checked) {
                    setAcceptedOutlierValues({});
                    return;
                  }
                  setAcceptedOutlierValues(prev => ({
                    ...prev,
                    ...Object.fromEntries(activeOutlierValues.map(({ key, value }) => [
                      key,
                      JSON.stringify([typeof value, value]),
                    ])),
                  }));
                }}
              >
                I have reviewed and confirm the accuracy of this information.
              </Checkbox>
            </div>
          )}
          <button
            className="btn-verify"
            type="button"
            disabled={!isVerifyEnabled}
            onClick={handleVerify}
          >
            {verifying ? 'Verifying...' : 'VERIFY'}
          </button>
        </div>

        {lastVerifiedDate && lastVerifierName && (
          <div className="verify-last-verified">
            Last verified by <strong>{lastVerifierName}</strong> on {formatDateTime(lastVerifiedDate)}
          </div>
        )}
      </>
    );
  };

  return (
    <div className="voucher-verify-page">
      <TopNavPublic />
      <div className="verify-card">
        {renderContent()}
      </div>
    </div>
  );
};

VoucherVerifyPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      dealId: PropTypes.string,
    }),
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func,
    goBack: PropTypes.func,
  }).isRequired,
  setShowGlobalNavigation: PropTypes.func,
  toast: PropTypes.func,
};

VoucherVerifyPage.defaultProps = {
  setShowGlobalNavigation: null,
  toast: null,
};

export default VoucherVerifyPage;
